import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { SelectData } from "../../../../../models";
import { searchStandards } from "../../../../../service/query";
import { searchWorkflowTemplates } from "../../../../../service/query/QueryService.full";
import { Status } from "../../../../../service/Shared";
import { useAuth } from "../../../../../useAuth";
import { getWorkflowTemplateRoute } from "../../../../../utils/routes";
import { dataGridMapFilterCriteria } from "../../../../../widget";
import { getWorkflowTemplateExpiryFilterGroups } from "../../../../shared/workflows";
import { WorkflowDataCardModel } from "../../../../shared/workflows/workflow-templates/components/WorkflowDataCards/models";
import { WorkflowTemplateTypeConstants } from "../constants";
import { UseDealWorkflowsReturnData } from "./models/UseDealWorkflowsReturnData";

const pageSize = 6;

export const useDealWorkflows = (): UseDealWorkflowsReturnData => {
  const navigate = useNavigate();
  const { currentUserType } = useAuth();

  const [isLoadingWorkflowTemplates, setIsLoadingWorkflowTemplates] = useState(true);
  const [workflowTemplates, setWorkflowTemplates] = useState<WorkflowDataCardModel[]>([]);
  const [relatedProjectWorkflowTemplateUuid, setRelatedProjectWorkflowTemplateUuid] = useState<string>();
  const [refreshWorkflowTemplates, setRefreshWorkflowTemplates] = useState(false);

  const [projectTypesSelectData, setProjectTypesSelectData] = useState<SelectData>([]);
  const [projectTypeFilterUuid, setProjectTypeFilterUuid] = useState<string | undefined | null>();
  const [isExpiredFilter, setIsExpiredFilter] = useState<boolean>(false);

  const [showCreateWorkflowTemplatePanel, setShowCreateWorkflowTemplatePanel] = useState(false);
  const previousFiltersRef = useRef({
    isExpiredFilter: false,
    projectTypeFilterUuid: undefined as string | undefined | null,
  });

  const [startCursor, setStartCursor] = useState<string | null>(null);
  const [endCursor, setEndCursor] = useState<string | null>(null);
  const [beforeCursor, setBeforeCursor] = useState<string | null>(null);
  const [afterCursor, setAfterCursor] = useState<string | null>(null);
  const [hasNext, setHasNext] = useState<boolean>(true);
  const [hasPrevious, setHasPrevious] = useState<boolean>(false);

  const onPrevious = (): void => {
    setBeforeCursor(startCursor);
    setAfterCursor(null);
  };

  const onNext = (): void => {
    setBeforeCursor(null);
    setAfterCursor(endCursor);
  };

  const fetchWorkflowTemplates = useCallback(async (): Promise<void> => {
    setIsLoadingWorkflowTemplates(true);
    setRelatedProjectWorkflowTemplateUuid(undefined);

    const filterCriteria = dataGridMapFilterCriteria([]);
    filterCriteria.type = {
      operator: "eq",
      value: WorkflowTemplateTypeConstants.DEAL,
    };

    if (projectTypeFilterUuid) {
      filterCriteria.projectType = {
        uuid: {
          operator: "eq",
          value: projectTypeFilterUuid,
        },
      };
    }
    const response = await searchWorkflowTemplates({
      ...getWorkflowTemplateExpiryFilterGroups(isExpiredFilter),
      paging: {
        limit: pageSize,
        beforeCursor,
        afterCursor,
      },
      filter: { results: filterCriteria },
    });

    if (response.status === Status.Success && response.data) {
      setStartCursor(response.data.paging.startCursor);
      setEndCursor(response.data.paging.endCursor);
      setHasPrevious(response.data.paging.hasPreviousPage);
      setHasNext(response.data.paging.hasNextPage);

      setWorkflowTemplates(
        response.data.results.map((workflowTemplate) => ({
          uuid: workflowTemplate.uuid,
          name: workflowTemplate.name,
          description: workflowTemplate.description,
          tasks: workflowTemplate.taskCount,
          organisationName: workflowTemplate.ownerOrganisation.name,
          projectTypeUuid: workflowTemplate.projectType.uuid ?? "",
          isExpired: workflowTemplate.validTo !== null && workflowTemplate.validTo < new Date(),
          onShowProjectsClick: () => setRelatedProjectWorkflowTemplateUuid(workflowTemplate.uuid),
          onViewWorkflowClick: () => navigate(getWorkflowTemplateRoute(workflowTemplate.uuid, currentUserType)),
        }))
      );
    }
  }, [afterCursor, beforeCursor, currentUserType, isExpiredFilter, navigate, projectTypeFilterUuid]);

  const fetchProjectTypes = async (): Promise<void> => {
    const response = await searchStandards({
      paging: {
        limit: 50,
        beforeCursor,
        afterCursor,
      },
    });

    if (response.status === Status.Success && response.data) {
      const projectTypeMap = new Map<string, { key: string; value: string }>();

      response.data.results.forEach((standard) => {
        standard.projectTypes.forEach((projectType) => {
          projectTypeMap.set(projectType.uuid, {
            key: projectType.uuid,
            value: projectType.displayName,
          });
        });
      });

      setProjectTypesSelectData(Array.from(projectTypeMap.values()) as SelectData);
    }
  };

  useEffect(() => {
    const previousFilters = previousFiltersRef.current;
    const hasFilterChanged =
      previousFilters.projectTypeFilterUuid !== projectTypeFilterUuid ||
      previousFilters.isExpiredFilter !== isExpiredFilter;

    previousFiltersRef.current = {
      projectTypeFilterUuid,
      isExpiredFilter,
    };

    if (hasFilterChanged && (beforeCursor !== null || afterCursor !== null)) {
      setIsLoadingWorkflowTemplates(true);
      setRelatedProjectWorkflowTemplateUuid(undefined);
      setBeforeCursor(null);
      setAfterCursor(null);
      return;
    }

    fetchWorkflowTemplates().finally(() => {
      setIsLoadingWorkflowTemplates(false);
    });
  }, [
    afterCursor,
    beforeCursor,
    fetchWorkflowTemplates,
    isExpiredFilter,
    projectTypeFilterUuid,
    refreshWorkflowTemplates,
  ]);

  useEffect(() => {
    fetchProjectTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    workflowTemplates,
    relatedProjectWorkflowTemplateUuid,
    projectTypesSelectData,
    isLoadingWorkflowTemplates,
    projectTypeFilterUuid,
    setProjectTypeFilterUuid,
    showCreateWorkflowTemplatePanel,
    setShowCreateWorkflowTemplatePanel,
    isExpiredFilter,
    setIsExpiredFilter,
    setRefreshWorkflowTemplates,
    onPrevious,
    onNext,
    hasPrevious,
    hasNext,
  };
};
